import { deserializeTransaction, isLegacyTransaction } from '@exodus/web3-solana-utils';
import { safeString } from '@exodus/safe-string';
import { ExchangeError } from '../errors.js';
const ORDER_ENGINE_PROGRAM_ID = '61DFfeTKM7trxYcPQCM78bJ794ddZprZpAwAnLiwTpYH';
const COMPUTE_BUDGET_PROGRAM_ID = 'ComputeBudget111111111111111111111111111111';
const ASSOCIATED_TOKEN_PROGRAM_ID = 'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL';
const FILL_DISCRIMINATOR = new Uint8Array([168, 96, 183, 163, 92, 10, 40, 160]);
const COMPUTE_BUDGET_SET_UNIT_LIMIT = 2;
const COMPUTE_BUDGET_SET_UNIT_PRICE = 3;
export const FillValidationErrorCode = {
    TOO_MANY_SIGNERS: 'TOO_MANY_SIGNERS',
    INVALID_TAKER_SIGNER: 'INVALID_TAKER_SIGNER',
    DUPLICATE_COMPUTE_UNIT_PRICE: 'DUPLICATE_COMPUTE_UNIT_PRICE',
    UNEXPECTED_COMPUTE_BUDGET_INSTRUCTION: 'UNEXPECTED_COMPUTE_BUDGET_INSTRUCTION',
    INCORRECT_ATA_DATA: 'INCORRECT_ATA_DATA',
    INVALID_ATA_PAYER: 'INVALID_ATA_PAYER',
    INVALID_ATA_OWNER: 'INVALID_ATA_OWNER',
    INVALID_ATA_MINT: 'INVALID_ATA_MINT',
    DUPLICATE_FILL_INSTRUCTION: 'DUPLICATE_FILL_INSTRUCTION',
    INVALID_FILL_DATA: 'INVALID_FILL_DATA',
    INVALID_FILL_DISCRIMINATOR: 'INVALID_FILL_DISCRIMINATOR',
    NOT_ENOUGH_ACCOUNTS: 'NOT_ENOUGH_ACCOUNTS',
    INVALID_TAKER: 'INVALID_TAKER',
    INVALID_MAKER: 'INVALID_MAKER',
    INVALID_INPUT_MINT: 'INVALID_INPUT_MINT',
    INVALID_OUTPUT_MINT: 'INVALID_OUTPUT_MINT',
    INVALID_FILL_DATA_LENGTH: 'INVALID_FILL_DATA_LENGTH',
    INVALID_FILL_AMOUNTS: 'INVALID_FILL_AMOUNTS',
    FILL_EXPIRED: 'FILL_EXPIRED',
    UNEXPECTED_PROGRAM_ID: 'UNEXPECTED_PROGRAM_ID',
    MISSING_FILL_INSTRUCTION: 'MISSING_FILL_INSTRUCTION',
};
export class FillValidationError extends ExchangeError {
    name = safeString `Fill Validation Error`;
    code;
    expected;
    actual;
    constructor({ code, message, expected, actual, }) {
        super({ message, details: { code, expected, actual } });
        this.code = code;
        this.expected = expected;
        this.actual = actual;
    }
}
function parseMessage(tx) {
    if (isLegacyTransaction(tx)) {
        return {
            numRequiredSignatures: tx.signatures.length,
            secondSigner: tx.signatures[1]?.publicKey.toBase58(),
            instructions: tx.instructions.map((ix) => ({
                programId: ix.programId.toBase58(),
                accountKeys: ix.keys.map((k) => k.pubkey.toBase58()),
                data: Uint8Array.from(ix.data),
            })),
        };
    }
    const { message } = tx;
    const accountKeys = message.staticAccountKeys;
    return {
        numRequiredSignatures: message.header.numRequiredSignatures,
        secondSigner: accountKeys[1]?.toBase58(),
        instructions: message.compiledInstructions.map((ix) => ({
            programId: accountKeys[ix.programIdIndex].toBase58(),
            accountKeys: ix.accountKeyIndexes.map((i) => accountKeys[i].toBase58()),
            data: ix.data,
        })),
    };
}
function readU64LE(data, offset) {
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    return view.getBigUint64(offset, true);
}
function readI64LE(data, offset) {
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    return view.getBigInt64(offset, true);
}
function uint8ArraysEqual(a, b) {
    if (a.length !== b.length)
        return false;
    for (const [i, byte] of a.entries()) {
        if (byte !== b[i])
            return false;
    }
    return true;
}
export function validateFillMessage(rawTx, order) {
    const tx = deserializeTransaction(rawTx);
    const { numRequiredSignatures, secondSigner, instructions } = parseMessage(tx);
    if (numRequiredSignatures !== 2) {
        throw new FillValidationError({
            code: FillValidationErrorCode.TOO_MANY_SIGNERS,
            message: `Expected 2 signers, got ${numRequiredSignatures}`,
            expected: '2',
            actual: String(numRequiredSignatures),
        });
    }
    if (secondSigner !== order.taker) {
        throw new FillValidationError({
            code: FillValidationErrorCode.INVALID_TAKER_SIGNER,
            message: 'Second transaction signer is not the taker',
            expected: order.taker,
            actual: secondSigner,
        });
    }
    const maker = instructions.find((ix) => ix.programId === ORDER_ENGINE_PROGRAM_ID)?.accountKeys[1];
    let fillIxFound = false;
    let computeUnitPriceSet = false;
    for (const ix of instructions) {
        if (ix.programId === COMPUTE_BUDGET_PROGRAM_ID) {
            const ixType = ix.data[0];
            if (ixType === COMPUTE_BUDGET_SET_UNIT_LIMIT) {
                continue;
            }
            if (ixType === COMPUTE_BUDGET_SET_UNIT_PRICE) {
                if (computeUnitPriceSet) {
                    throw new FillValidationError({
                        code: FillValidationErrorCode.DUPLICATE_COMPUTE_UNIT_PRICE,
                        message: 'Compute unit price is already set',
                    });
                }
                computeUnitPriceSet = true;
                continue;
            }
            throw new FillValidationError({
                code: FillValidationErrorCode.UNEXPECTED_COMPUTE_BUDGET_INSTRUCTION,
                message: 'Unexpected compute budget instruction',
            });
        }
        if (ix.programId === ASSOCIATED_TOKEN_PROGRAM_ID) {
            if (ix.data.length !== 1 || ix.data[0] !== 1) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INCORRECT_ATA_DATA,
                    message: 'Incorrect associated token account program data',
                });
            }
            if (ix.accountKeys[0] !== order.taker) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INVALID_ATA_PAYER,
                    message: 'Taker must pay for token account creation',
                    expected: order.taker,
                    actual: ix.accountKeys[0],
                });
            }
            if (ix.accountKeys[2] !== order.taker && ix.accountKeys[2] !== maker) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INVALID_ATA_OWNER,
                    message: 'ATA owner must be the taker or the maker',
                    expected: maker === undefined ? order.taker : `${order.taker} or ${maker}`,
                    actual: ix.accountKeys[2],
                });
            }
            if (ix.accountKeys[3] !== order.inputMint && ix.accountKeys[3] !== order.outputMint) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INVALID_ATA_MINT,
                    message: 'ATA mint must be the swap input or output mint',
                    expected: `${order.inputMint} or ${order.outputMint}`,
                    actual: ix.accountKeys[3],
                });
            }
            continue;
        }
        if (ix.programId === ORDER_ENGINE_PROGRAM_ID) {
            if (fillIxFound) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.DUPLICATE_FILL_INSTRUCTION,
                    message: 'Duplicated fill instruction',
                });
            }
            fillIxFound = true;
            if (ix.data.length < 8) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INVALID_FILL_DATA,
                    message: 'Not enough data in fill instruction',
                });
            }
            if (!uint8ArraysEqual(ix.data.slice(0, 8), FILL_DISCRIMINATOR)) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INVALID_FILL_DISCRIMINATOR,
                    message: 'Not a fill discriminator',
                });
            }
            if (ix.accountKeys.length < 10) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.NOT_ENOUGH_ACCOUNTS,
                    message: 'Not enough accounts',
                    expected: '10',
                    actual: String(ix.accountKeys.length),
                });
            }
            if (ix.accountKeys[0] !== order.taker) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INVALID_TAKER,
                    message: 'Invalid taker',
                    expected: order.taker,
                    actual: ix.accountKeys[0],
                });
            }
            if (ix.accountKeys[6] !== order.inputMint) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INVALID_INPUT_MINT,
                    message: 'Invalid input mint',
                    expected: order.inputMint,
                    actual: ix.accountKeys[6],
                });
            }
            if (ix.accountKeys[8] !== order.outputMint) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INVALID_OUTPUT_MINT,
                    message: 'Invalid output mint',
                    expected: order.outputMint,
                    actual: ix.accountKeys[8],
                });
            }
            if (ix.data.length < 32) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INVALID_FILL_DATA_LENGTH,
                    message: 'Invalid fill instruction data length',
                    expected: '32',
                    actual: String(ix.data.length),
                });
            }
            const inputAmount = readU64LE(ix.data, 8);
            const outputAmount = readU64LE(ix.data, 16);
            if (inputAmount !== order.inAmount || outputAmount !== order.outAmount) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.INVALID_FILL_AMOUNTS,
                    message: 'Fill instruction input/output amounts do not match expected order amounts',
                    expected: `${order.inAmount}/${order.outAmount}`,
                    actual: `${inputAmount}/${outputAmount}`,
                });
            }
            const expireAt = readI64LE(ix.data, 24);
            const nowSeconds = BigInt(Math.floor(Date.now() / 1000));
            if (expireAt <= nowSeconds) {
                throw new FillValidationError({
                    code: FillValidationErrorCode.FILL_EXPIRED,
                    message: 'Fill instruction has expired',
                    expected: `> ${nowSeconds}`,
                    actual: String(expireAt),
                });
            }
            continue;
        }
        throw new FillValidationError({
            code: FillValidationErrorCode.UNEXPECTED_PROGRAM_ID,
            message: `Unexpected program id ${ix.programId}`,
            actual: ix.programId,
        });
    }
    if (!fillIxFound) {
        throw new FillValidationError({
            code: FillValidationErrorCode.MISSING_FILL_INSTRUCTION,
            message: 'Missing fill instruction',
        });
    }
}
